/**
 * 루미나 화면 층 신작 — 블랙 components/common/UnreadNoticeModal.jsx 의 로직 그대로, JSX·CSS 만 교체(시안 40-overlays 읽지 않은 쪽지 모달 um-).
 * WHY: SPEC §2 — 문구 4종·버튼 3개(쪽지 페이지로 이동·전체 읽기·닫기)·트리거(마운트·소켓·게임 실행 403)·닫기 후 재확인은 원본 그대로.
 *   모양만 키트 모달 규격(흰 24px 카드, 주 버튼 차콜 / 전체 읽기 민트 / 닫기 고스트, 봉투 아이콘은 인라인 SVG).
 *
 * UnreadNoticeModal — 미읽음 쪽지 강제 노출 모달 (2026-05-06 신설)
 *
 * [쪽지/고객센터 분리]
 *   요구사항(D):
 *   - 로그인 시 미읽음 쪽지 1건 이상 → 모달 강제 노출 + "쪽지 페이지로 이동" 버튼
 *   - 모달 닫기 가능. 닫으면 system_settings.notice_modal_recheck_seconds(기본 5초) 후
 *     자동 재노출. 미읽음이 0건이 되어야 더 이상 뜨지 않음.
 *     쪽지 화면에서는 재노출하지 않고 그 화면을 떠날 때 다시 판단한다. 게임 화면이 열려 있으면 재노출하지 않고 게임을 닫은 뒤 재개한다.
 *   - 게임 런치 시도 → 백엔드 403(UNREAD_NOTICE_GATE) 응답 → 클라이언트가 모달 노출.
 *   - 게임 중 새 쪽지 도착 → Socket.IO 'message:new' (message_type='notice') 수신 →
 *     게임창 위에 모달 강제 노출.
 *
 * 동작 흐름:
 *   1) 마운트 시 useUnreadCount() 의 gateNoticeUnread > 0 이면 모달 ON
 *   2) Socket.IO 'message:new' 수신 + payload.message_type === 'notice' → 모달 ON
 *   3) window 'force-unread-notice-modal' 이벤트 → 모달 ON (게임 런치 403 시 트리거)
 *   4) 닫기 버튼 → 모달 OFF + recheck_seconds 후 재체크 → 여전히 미읽음 있으면 재노출
 *   5) "쪽지 페이지로 이동" 버튼 → /mypage/customer 로 라우팅 + 모달 OFF
 *
 * 다른 화면·기능에 영향 없음 (전역 마운트지만 gateNoticeUnread=0 이면 비노출).
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import useAuth from '../../hooks/useAuth';
import useUnreadCount from '../../hooks/useUnreadCount';
import { isGameScreenOpen } from '../../utils/gameScreenOpen';
import './UnreadNoticeModal.css';

// 기본 재노출 간격(초). DB system_settings.notice_modal_recheck_seconds 로
// 운영자가 조정 가능. 응답 실패·미설정 시 fallback 기본값 5초.
const FALLBACK_RECHECK_SECONDS = 5;
// 쪽지를 읽는 화면(이 화면에서는 닫은 창을 다시 띄우지 않는다)
const NOTICE_PAGE_PATH = '/mypage/customer';

const UnreadNoticeModal = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { gateNoticeUnread, refetch } = useUnreadCount();

  const [isOpen, setIsOpen] = useState(false);
  // [2026-11-01] 게임 진입 거절 응답이 알려 준 건수 — 숫자 재조회가 실패해도 창이 떠야 한다(호출부 알림창은 생략되므로 무반응이 된다)
  const [gateUnread, setGateUnread] = useState(0);
  const [recheckSeconds, setRecheckSeconds] = useState(FALLBACK_RECHECK_SECONDS);
  const [readAllFailed, setReadAllFailed] = useState(false);
  const recheckTimerRef = useRef(null);
  const location = useLocation();
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  // [2026-11-03] 재노출 간격(system_settings.notice_modal_recheck_seconds)을 로그인 뒤 한 번 받는다 — 실패하면 처음 값으로 둔다
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    api.get('/messages/notice-settings')
      .then((res) => {
        const seconds = res.data?.data?.recheckSeconds;
        if (!cancelled && seconds) setRecheckSeconds(seconds);
      })
      .catch(() => { /* 창 동작에 영향 없는 값이라 처음 값으로 계속 간다 */ });
    return () => { cancelled = true; };
  }, [user]);

  const clearRecheckTimer = useCallback(() => {
    if (recheckTimerRef.current) clearTimeout(recheckTimerRef.current);
    recheckTimerRef.current = null;
  }, []);
  useEffect(() => clearRecheckTimer, [clearRecheckTimer]);

  // 미읽음 쪽지가 늘면(첫 조회 포함) 모달 자동 ON, 0 이 되면 OFF.
  //   WHY 늘 때만 여는가: 종전엔 숫자가 바뀔 때마다 열어, 쪽지 화면에서 여러 건을 차례로 읽으면
  //     한 건 읽을 때마다 창이 다시 떠 방금 펼친 본문을 가렸다(2026-11-01 독립 검토).
  //   숫자가 실제로 0 이 되면(다른 기기에서 읽음 포함) 거절 건수도 지운다 — 옛 건수로 창이 남거나 다시 뜨지 않게 한다.
  const prevNoticeUnreadRef = useRef(0);
  useEffect(() => {
    if (!user) {
      prevNoticeUnreadRef.current = 0;   // 다시 로그인하면 첫 조회로 다시 판단한다
      return;
    }
    const prev = prevNoticeUnreadRef.current;
    prevNoticeUnreadRef.current = gateNoticeUnread;
    if (gateNoticeUnread > prev) {
      setIsOpen(true);
    } else if (gateNoticeUnread <= 0 && prev > 0) {
      setIsOpen(false);
      setGateUnread(0);
    }
  }, [gateNoticeUnread, user]);

  // 외부 트리거: 게임 런치 403 시 window 이벤트로 모달 강제 노출.
  //   (게임 클릭 핸들러가 dispatchEvent(new CustomEvent('force-unread-notice-modal')) 발사)
  useEffect(() => {
    const onForce = (event) => {
      clearRecheckTimer();
      setGateUnread(Number(event?.detail?.unreadNoticeCount) || 0);
      refetch();
      setIsOpen(true);
    };
    window.addEventListener('force-unread-notice-modal', onForce);
    return () => window.removeEventListener('force-unread-notice-modal', onForce);
  }, [refetch, clearRecheckTimer]);

  // Socket.IO 'message:new' 수신 (message_type='notice') → 게임창 위에 강제 노출.
  //   기존 코드가 window 'message:new' 이벤트를 발생시키므로 여기서 listen.
  useEffect(() => {
    const onMessageNew = (event) => {
      const detail = event.detail || {};
      if (detail.message_type === 'notice' || !detail.message_type) {
        refetch();
        setIsOpen(true);
      }
    };
    window.addEventListener('message:new', onMessageNew);
    return () => window.removeEventListener('message:new', onMessageNew);
  }, [refetch]);

  // 닫은 창을 다시 띄울지 판단한다 — 안 읽은 쪽지가 남아 있을 때만(요구 D — 0건이 되어야 더 이상 뜨지 않는다).
  //   쪽지 화면에서는 띄우지 않는다 — 읽는 중인 본문을 가린다(2026-11-01 독립 검토). 그 화면을 떠날 때 아래 효과가 다시 판단한다.
  //   게임 화면이 열려 있으면 띄우지 않고 다음 차례로 미룬다 — 간격마다 게임 위에 떠 베팅을 방해했다(2026-11-03 독립 검토). 게임을 닫으면 다음 차례에 뜬다.
  //   새 쪽지가 왔을 때 한 번 뜨는 것은 위 효과들의 몫이라 게임 중에도 그대로다.
  const recheckRef = useRef(null);
  const armRecheck = useCallback(() => {
    clearRecheckTimer();
    const sec = Math.max(1, parseInt(recheckSeconds, 10) || FALLBACK_RECHECK_SECONDS);
    recheckTimerRef.current = setTimeout(() => recheckRef.current(), sec * 1000);
  }, [recheckSeconds, clearRecheckTimer]);
  const recheck = useCallback(async () => {
    clearRecheckTimer();
    if (isGameScreenOpen()) {
      armRecheck();
      return;
    }
    const latest = await refetch();
    if (latest.gateNoticeUnread > 0 && pathRef.current !== NOTICE_PAGE_PATH) setIsOpen(true);
  }, [refetch, clearRecheckTimer, armRecheck]);
  recheckRef.current = recheck;

  // 닫기 → recheckSeconds 뒤 다시 판단한다.
  const handleClose = useCallback(() => {
    setIsOpen(false);
    setGateUnread(0);
    setReadAllFailed(false);
    armRecheck();
  }, [armRecheck]);

  // 쪽지 화면을 떠날 때 다시 판단한다 — 그 화면에서는 띄우지 않으므로, 타이머가 거기서 끝났거나 [쪽지 페이지로 이동]으로 온 뒤 안 읽고 떠나면 다시 뜰 차례가 없었다.
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    const leftNoticePage = prevPathRef.current === NOTICE_PAGE_PATH && location.pathname !== NOTICE_PAGE_PATH;
    prevPathRef.current = location.pathname;
    if (leftNoticePage && user) recheck();
  }, [location.pathname, user, recheck]);

  // 쪽지 페이지로 이동
  const handleGoToNotices = useCallback(() => {
    clearRecheckTimer();
    setIsOpen(false);
    setGateUnread(0);
    setReadAllFailed(false);
    // 이미 쪽지 화면이면 경로가 같아 다시 그려지지 않고, 숫자 조회 시점도 창과 화면이 달라 최대 60초 어긋난다 — 목록을 다시 받으라고 알린다(독립 검토)
    window.dispatchEvent(new CustomEvent('unread-notice-goto-list'));
    navigate(NOTICE_PAGE_PATH);
  }, [navigate, clearRecheckTimer]);

  // 전체 읽기 (선택적 — 모달 안에서 한 번에 처리 가능)
  const handleReadAll = useCallback(async () => {
    setReadAllFailed(false);
    try {
      await api.put('/messages/notices/read-all');
      await refetch(); // gateNoticeUnread → 0 → 자동 닫힘
      setGateUnread(0);
      setIsOpen(false);
    } catch (err) {
      // [2026-11-03] 창 안에 알린다 — 창이 공용 알림창보다 위 층이라 알림창을 띄우면 이 창에 가린다. 종전엔 눌러도 아무 반응이 없었다
      console.error('전체 읽기 실패:', err);
      setReadAllFailed(true);
    }
  }, [refetch]);

  const shownUnread = gateNoticeUnread > 0 ? gateNoticeUnread : gateUnread;
  if (!user || !isOpen || shownUnread <= 0) return null;

  return (
    <div className="um-ov" role="dialog" aria-modal="true" aria-labelledby="umTitle">
      <div className="um-panel">
        <div className="um-ic">
          <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="3" /><path d="M3 8l9 6 9-6" /></svg>
        </div>
        <h3 className="um-title" id="umTitle">읽지 않은 쪽지</h3>
        <p className="um-body">
          확인하지 않은 쪽지가 <strong>{shownUnread}</strong>건 있습니다.<br />
          쪽지를 모두 읽은 후에 게임 진행이 가능합니다.
        </p>
        {readAllFailed && <p className="um-error" role="alert">전체 읽기에 실패했습니다. 잠시 후 다시 시도해 주세요.</p>}
        <div className="um-acts">
          <button type="button" className="lk-btn block" onClick={handleGoToNotices}>
            쪽지 페이지로 이동
          </button>
          <button type="button" className="lk-btn mint block" onClick={handleReadAll}>
            전체 읽기
          </button>
          <button type="button" className="lk-btn ghost block" onClick={handleClose}>
            닫기
          </button>
        </div>
      </div>
    </div>
  );
};

export default UnreadNoticeModal;
